import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Row, Col, Spinner, Alert } from 'reactstrap';
import eventApi from '../../../../api/CheckInEvent';

const revokeObjectUrls = (urls) => {
  if (!Array.isArray(urls)) return;
  urls.forEach((url) => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};

const initialPreviewState = {
  eventImages: [],
  descriptionImages: []
};

// Constants for validation
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_DESCRIPTION_LENGTH = 5000;
const MAX_TITLE_LENGTH = 200;

const CreateEventModal = ({ isOpen, toggle, onEventCreated }) => {
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    organizer: '',
    type: 'offline',
    descriptionText: '',
    startTime: '',
    endTime: '',
    maxAttendees: '',
    link: '',
    eventImages: [],
    descriptionImages: []
  });
  const [previewImages, setPreviewImages] = useState(initialPreviewState);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    return () => {
      revokeObjectUrls(previewImages.eventImages);
      revokeObjectUrls(previewImages.descriptionImages);
    };
  }, [previewImages]);

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setSubmitError('');
    }
  }, [isOpen]);

  // Validation functions
  const validateTitle = (title) => {
    if (!title || !title.trim()) {
      return 'Tiêu đề sự kiện là bắt buộc';
    }
    if (title.trim().length < 3) {
      return 'Tiêu đề phải có ít nhất 3 ký tự';
    }
    if (title.length > MAX_TITLE_LENGTH) {
      return `Tiêu đề không được vượt quá ${MAX_TITLE_LENGTH} ký tự`;
    }
    return '';
  };

  const validateDate = (date) => {
    if (!date) {
      return 'Ngày tổ chức là bắt buộc';
    }
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      return 'Ngày tổ chức không được trong quá khứ';
    }
    return '';
  };

  const validateLocation = (location) => {
    if (!location || !location.trim()) {
      return 'Địa điểm là bắt buộc';
    }
    if (location.trim().length < 3) {
      return 'Địa điểm phải có ít nhất 3 ký tự';
    }
    return '';
  };

  const validateTimeRange = (startTime, endTime, date) => {
    if (!startTime && !endTime) {
      return ''; // Both optional
    }
    
    if (startTime && !endTime) {
      return 'Vui lòng nhập thời gian kết thúc';
    }
    
    if (!startTime && endTime) {
      return 'Vui lòng nhập thời gian bắt đầu';
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    if (end <= start) {
      return 'Thời gian kết thúc phải sau thời gian bắt đầu';
    }

    // Check if start time is not in the past if date is today
    if (date) {
      const eventDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);
      
      if (eventDate.getTime() === today.getTime()) {
        const now = new Date();
        if (start < now) {
          return 'Thời gian bắt đầu không được trong quá khứ';
        }
      }
    }

    return '';
  };

  const validateMaxAttendees = (maxAttendees) => {
    if (!maxAttendees || maxAttendees === '') {
      return ''; // Optional
    }
    const num = parseInt(maxAttendees, 10);
    if (isNaN(num) || num < 1) {
      return 'Số lượng người tham gia phải là số nguyên dương';
    }
    if (num > 1000000) {
      return 'Số lượng người tham gia không được vượt quá 1,000,000';
    }
    return '';
  };

  const validateLink = (link) => {
    if (!link || !link.trim()) {
      return ''; // Optional
    }
    try {
      new URL(link.trim());
      return '';
    } catch {
      return 'Link không hợp lệ. Vui lòng nhập URL đầy đủ (ví dụ: https://example.com)';
    }
  };

  const validateDescription = (description) => {
    if (!description || !description.trim()) {
      return 'Mô tả sự kiện là bắt buộc';
    }
    if (description.trim().length < 10) {
      return 'Mô tả phải có ít nhất 10 ký tự';
    }
    if (description.length > MAX_DESCRIPTION_LENGTH) {
      return `Mô tả không được vượt quá ${MAX_DESCRIPTION_LENGTH} ký tự`;
    }
    return '';
  };

  const validateFiles = (files, fieldName, maxFiles = Infinity) => {
    if (!files || files.length === 0) {
      return ''; // Optional
    }

    if (files.length > maxFiles) {
      return fieldName === 'eventImages' 
        ? 'Chỉ được chọn 1 ảnh cho sự kiện' 
        : `Chỉ được chọn tối đa ${maxFiles} ảnh`;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        return `File ${file.name} không đúng định dạng. Chỉ chấp nhận: JPEG, PNG, GIF, WebP`;
      }
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        return `File ${file.name} quá lớn. Kích thước tối đa là 5MB`;
      }
    }

    return '';
  };

  // Validate single field
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'title':
        error = validateTitle(value);
        break;
      case 'date':
        error = validateDate(value);
        // Also validate time range if dates change
        if (!error && (formData.startTime || formData.endTime)) {
          error = validateTimeRange(formData.startTime, formData.endTime, value);
          // Update both time errors if needed
          if (error) {
            setErrors(prev => ({
              ...prev,
              startTime: error,
              endTime: error
            }));
          }
        }
        break;
      case 'location':
        error = validateLocation(value);
        break;
      case 'startTime':
      case 'endTime': {
        // Use updated value for the field being changed
        const currentStartTime = name === 'startTime' ? value : formData.startTime;
        const currentEndTime = name === 'endTime' ? value : formData.endTime;
        error = validateTimeRange(currentStartTime, currentEndTime, formData.date);
        // Update both time errors since they're related
        setErrors(prev => ({
          ...prev,
          startTime: error,
          endTime: error
        }));
        return !error;
      }
      case 'maxAttendees':
        error = validateMaxAttendees(value);
        break;
      case 'link':
        error = validateLink(value);
        break;
      case 'descriptionText':
        error = validateDescription(value);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate required fields
    const titleError = validateTitle(formData.title);
    if (titleError) {
      newErrors.title = titleError;
      isValid = false;
    }

    const dateError = validateDate(formData.date);
    if (dateError) {
      newErrors.date = dateError;
      isValid = false;
    }

    const locationError = validateLocation(formData.location);
    if (locationError) {
      newErrors.location = locationError;
      isValid = false;
    }

    const descriptionError = validateDescription(formData.descriptionText);
    if (descriptionError) {
      newErrors.descriptionText = descriptionError;
      isValid = false;
    }

    // Validate optional fields
    const timeRangeError = validateTimeRange(formData.startTime, formData.endTime, formData.date);
    if (timeRangeError) {
      newErrors.startTime = timeRangeError;
      newErrors.endTime = timeRangeError;
      isValid = false;
    }

    const maxAttendeesError = validateMaxAttendees(formData.maxAttendees);
    if (maxAttendeesError) {
      newErrors.maxAttendees = maxAttendeesError;
      isValid = false;
    }

    const linkError = validateLink(formData.link);
    if (linkError) {
      newErrors.link = linkError;
      isValid = false;
    }

    const eventImagesError = validateFiles(formData.eventImages, 'eventImages', 1);
    if (eventImagesError) {
      newErrors.eventImages = eventImagesError;
      isValid = false;
    }

    const descriptionImagesError = validateFiles(formData.descriptionImages, 'descriptionImages', 10);
    if (descriptionImagesError) {
      newErrors.descriptionImages = descriptionImagesError;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError('');
    }
    
    // Validate field on change
    validateField(name, value);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const fileArray = Array.from(files);
    
    // Validate files
    const maxFiles = name === 'eventImages' ? 1 : 10;
    const fileError = validateFiles(fileArray, name, maxFiles);
    
    setErrors(prev => ({
      ...prev,
      [name]: fileError
    }));

    if (!fileError) {
      setFormData(prev => ({
        ...prev,
        [name]: fileArray
      }));

      setPreviewImages(prev => {
        revokeObjectUrls(prev[name]);
        return {
          ...prev,
          [name]: fileArray.map(file => URL.createObjectURL(file))
        };
      });
    } else {
      // Clear file input if validation fails
      e.target.value = '';
    }
    
    // Clear submit error when user selects files
    if (submitError) {
      setSubmitError('');
    }
  };

  const resetPreviewImages = () => {
    revokeObjectUrls(previewImages.eventImages);
    revokeObjectUrls(previewImages.descriptionImages);
    setPreviewImages(initialPreviewState);
  };

  // Reset form
  const resetForm = () => {
    resetPreviewImages();
    setFormData({
      title: '',
      date: '',
      location: '',
      organizer: '',
      type: 'offline',
      descriptionText: '',
      startTime: '',
      endTime: '',
      maxAttendees: '',
      link: '',
      eventImages: [],
      descriptionImages: []
    });
    setErrors({});
    setSubmitError('');
  };

  // Handle modal close
  const handleToggle = () => {
    if (!creating) {
      resetForm();
      toggle();
    }
  };

  // Create new event
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    // Validate entire form
    if (!validateForm()) {
      setSubmitError('Vui lòng kiểm tra và sửa các lỗi trong form trước khi tiếp tục');
      return;
    }

    try {
      setCreating(true);
      setSubmitError('');

      // Create event data object (only include fields with values)
      const eventData = {
        title: formData.title.trim(),
        date: formData.date,
        location: formData.location.trim(),
        type: formData.type,
        organizer: formData.organizer.trim() || undefined,
        descriptionText: formData.descriptionText.trim() || undefined,
        startTime: formData.startTime || undefined,
        endTime: formData.endTime || undefined,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees, 10) : undefined,
        link: formData.link.trim() || undefined,
        eventImages: formData.eventImages.length > 0 ? formData.eventImages : undefined,
        descriptionImages: formData.descriptionImages.length > 0 ? formData.descriptionImages : undefined
      };

      
      // Call API
      const result = await eventApi.create(eventData);
      
      // Reset form and close modal
      resetForm();
      toggle();
      alert('Tạo sự kiện thành công!');
      
      // Callback to parent to reload events
      if (onEventCreated) {
        onEventCreated();
      }
      
    } catch (err) {
      console.error('Error creating event:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setSubmitError(`Lỗi khi tạo sự kiện: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
      <ModalHeader toggle={handleToggle}>Tạo sự kiện mới</ModalHeader>
      <Form onSubmit={handleCreateEvent}>
        <ModalBody>
          {submitError && (
            <Alert color="danger" className="mb-3">
              {submitError}
            </Alert>
          )}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Tiêu đề sự kiện *</Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Nhập tiêu đề sự kiện"
                  invalid={!!errors.title}
                  maxLength={MAX_TITLE_LENGTH}
                />
                {errors.title && <div className="text-danger small mt-1">{errors.title}</div>}
                <small className="text-muted">{formData.title.length}/{MAX_TITLE_LENGTH} ký tự</small>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Ngày tổ chức *</Label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  invalid={!!errors.date}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.date && <div className="text-danger small mt-1">{errors.date}</div>}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Thời gian bắt đầu</Label>
                <Input
                  type="datetime-local"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  invalid={!!errors.startTime}
                  min={formData.date ? `${formData.date}T00:00` : undefined}
                />
                {errors.startTime && <div className="text-danger small mt-1">{errors.startTime}</div>}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Thời gian kết thúc</Label>
                <Input
                  type="datetime-local"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  invalid={!!errors.endTime}
                  min={formData.startTime || (formData.date ? `${formData.date}T00:00` : undefined)}
                />
                {errors.endTime && <div className="text-danger small mt-1">{errors.endTime}</div>}
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <Label>Địa điểm *</Label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Nhập địa điểm tổ chức"
                  invalid={!!errors.location}
                />
                {errors.location && <div className="text-danger small mt-1">{errors.location}</div>}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Đơn vị tổ chức</Label>
                <Input
                  type="text"
                  name="organizer"
                  value={formData.organizer}
                  onChange={handleInputChange}
                  placeholder="Nhập tên đơn vị tổ chức"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Loại sự kiện</Label>
                <Input
                  type="select"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="offline">Offline</option>
                  <option value="online">Online</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Số lượng người tham gia</Label>
                <Input
                  type="number"
                  name="maxAttendees"
                  value={formData.maxAttendees}
                  onChange={handleInputChange}
                  placeholder="1000"
                  min="1"
                  max="1000000"
                  invalid={!!errors.maxAttendees}
                />
                {errors.maxAttendees && <div className="text-danger small mt-1">{errors.maxAttendees}</div>}
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <Label>Mô tả sự kiện *</Label>
                <Input
                  type="textarea"
                  name="descriptionText"
                  value={formData.descriptionText}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Nhập mô tả chi tiết về sự kiện (tối thiểu 10 ký tự)"
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  invalid={!!errors.descriptionText}
                />
                {errors.descriptionText && <div className="text-danger small mt-1">{errors.descriptionText}</div>}
                <small className="text-muted">{formData.descriptionText.length}/{MAX_DESCRIPTION_LENGTH} ký tự (tối thiểu 10 ký tự)</small>
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <Label>Link (Zalo, Website...)</Label>
                <Input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  placeholder="https://zalo.me/g/..."
                  invalid={!!errors.link}
                />
                {errors.link && <div className="text-danger small mt-1">{errors.link}</div>}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Ảnh sự kiện</Label>
                <Input
                  type="file"
                  name="eventImages"
                  onChange={handleFileChange}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  invalid={!!errors.eventImages}
                />
                <small className="text-muted">Chỉ 1 ảnh cho sự kiện (tối đa 5MB)</small>
                {errors.eventImages && <div className="text-danger small mt-1">{errors.eventImages}</div>}
                {previewImages.eventImages.length > 0 && (
                  <div className="mt-2 d-flex gap-2 flex-wrap">
                    {previewImages.eventImages.map((src, index) => (
                      <img
                        key={`${src}-${index}`}
                        src={src}
                        alt="Ảnh sự kiện"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '0.5rem' }}
                      />
                    ))}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Ảnh mô tả</Label>
                <Input
                  type="file"
                  name="descriptionImages"
                  onChange={handleFileChange}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  multiple
                  invalid={!!errors.descriptionImages}
                />
                <small className="text-muted">Có thể chọn nhiều ảnh (tối đa 10 ảnh, mỗi ảnh 5MB)</small>
                {errors.descriptionImages && <div className="text-danger small mt-1">{errors.descriptionImages}</div>}
                {previewImages.descriptionImages.length > 0 && (
                  <div className="mt-2 d-flex gap-2 flex-wrap">
                    {previewImages.descriptionImages.map((src, index) => (
                      <img
                        key={`${src}-${index}`}
                        src={src}
                        alt="Ảnh mô tả"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '0.5rem' }}
                      />
                    ))}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleToggle} disabled={creating}>
            Hủy
          </Button>
          <Button color="primary" type="submit" disabled={creating || Object.keys(errors).some(key => errors[key])}>
            {creating ? (
              <>
                <Spinner size="sm" className="me-1" />
                Đang tạo...
              </>
            ) : (
              'Tạo sự kiện'
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateEventModal;

